import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_LIFETIME_MARGIN_CUMULS } from '@bloomays-lib/adapter.api-talent';
import { useAnnualTamPrimesStore } from '../stores/annualTamPrimesStore';
import type { AnnualTamPrimesState } from '../stores/annualTamPrimesStore';
import { GetLifetimeMarginCumulsQueryData, LifetimeMarginCumulAggregate } from '../types/primes';
import { buildLifetimeMarginCacheKey } from '../utils/annualTamPrimesCache.utils';
import { findLifetimeCumulForTam } from '../utils/lifetimeMargin.utils';

const persistLifetimeCumulsToStore = (
  cumuls: LifetimeMarginCumulAggregate[],
  setLifetimeCache: AnnualTamPrimesState['setLifetimeCache'],
  setLifetimeCumulsBatchCache: AnnualTamPrimesState['setLifetimeCumulsBatchCache'],
) => {
  setLifetimeCumulsBatchCache(cumuls);
  cumuls.forEach((cumul) => {
    setLifetimeCache(buildLifetimeMarginCacheKey(cumul.tam), cumul);
  });
};

export const useLifetimeMarginCumul = (tamEmail: string | undefined) => {
  const trimmedTamEmail = tamEmail?.trim() ?? '';
  const cacheKey = useMemo(
    () => (trimmedTamEmail ? buildLifetimeMarginCacheKey(trimmedTamEmail) : ''),
    [trimmedTamEmail],
  );
  const cachedEntry = useAnnualTamPrimesStore((state) => (cacheKey ? state.getLifetimeCache(cacheKey) : null));
  const setLifetimeCache = useAnnualTamPrimesStore((state) => state.setLifetimeCache);
  const setLifetimeCumulsBatchCache = useAnnualTamPrimesStore((state) => state.setLifetimeCumulsBatchCache);
  const skip = !trimmedTamEmail;

  const { loading, error, data } = useQuery<GetLifetimeMarginCumulsQueryData>(GET_LIFETIME_MARGIN_CUMULS, {
    skip,
    context: { clientName: 'api.talents' },
    fetchPolicy: cachedEntry ? 'cache-and-network' : 'network-only',
  });

  useEffect(() => {
    const cumuls = data?.getLifetimeMarginCumuls;
    if (!cumuls?.length) {
      return;
    }

    persistLifetimeCumulsToStore(cumuls, setLifetimeCache, setLifetimeCumulsBatchCache);
  }, [data?.getLifetimeMarginCumuls, setLifetimeCache, setLifetimeCumulsBatchCache]);

  const networkCumul = data?.getLifetimeMarginCumuls
    ? findLifetimeCumulForTam(data.getLifetimeMarginCumuls, trimmedTamEmail)
    : null;
  const cumul = networkCumul ?? cachedEntry?.cumul ?? null;
  const isLoading = cachedEntry === null && loading;

  return {
    cumul,
    loading: isLoading,
    error,
  };
};

export const useLifetimeMarginCumuls = () => {
  const cachedBatch = useAnnualTamPrimesStore((state) => state.lifetimeCumulsBatch);
  const setLifetimeCache = useAnnualTamPrimesStore((state) => state.setLifetimeCache);
  const setLifetimeCumulsBatchCache = useAnnualTamPrimesStore((state) => state.setLifetimeCumulsBatchCache);
  const hasCachedBatch = cachedBatch !== null;

  const { loading, error, data } = useQuery<GetLifetimeMarginCumulsQueryData>(GET_LIFETIME_MARGIN_CUMULS, {
    context: { clientName: 'api.talents' },
    fetchPolicy: hasCachedBatch ? 'cache-and-network' : 'network-only',
  });

  useEffect(() => {
    const cumuls = data?.getLifetimeMarginCumuls;
    if (!cumuls?.length) {
      return;
    }

    persistLifetimeCumulsToStore(cumuls, setLifetimeCache, setLifetimeCumulsBatchCache);
  }, [data?.getLifetimeMarginCumuls, setLifetimeCache, setLifetimeCumulsBatchCache]);

  const networkCumuls = data?.getLifetimeMarginCumuls ?? [];
  const cumuls = networkCumuls.length > 0 ? networkCumuls : cachedBatch?.cumuls ?? [];

  return {
    cumuls,
    loading: !hasCachedBatch && loading,
    error,
  };
};
